import { useEffect, useMemo, useState } from "react";
import { api, AssetEntry } from "../api";
import { formatVND, formatDate } from "../utils";
import {
  GlassPanel,
  KpiCard,
  Badge,
  Modal,
  DataTable,
  toast,
  confirm,
  SkeletonTable,
  ErrorState,
} from "../components";
import type { Column } from "../components/DataTable";

type AssetCategory = AssetEntry["category"];
type AssetStatus = AssetEntry["status"];

const CATEGORIES: { value: AssetCategory; label: string }[] = [
  { value: "equipment", label: "Máy móc" },
  { value: "furniture", label: "Nội thất" },
  { value: "electronics", label: "Điện tử" },
  { value: "tool", label: "Dụng cụ" },
  { value: "other", label: "Khác" },
];

const STATUS_MAP: Record<AssetStatus, { label: string; variant: "ok" | "warn" | "bad" }> = {
  active: { label: "Đang dùng", variant: "ok" },
  broken: { label: "Hỏng", variant: "warn" },
  disposed: { label: "Đã thanh lý", variant: "bad" },
};

const catLabel = (v: AssetCategory) => CATEGORIES.find((c) => c.value === v)?.label || v;

function currentValue(asset: AssetEntry): number {
  const yearsElapsed =
    (Date.now() - new Date(asset.purchase_date).getTime()) / (365 * 24 * 3600 * 1000);
  const depreciatedRatio = Math.min(1, yearsElapsed / Math.max(1, asset.depreciation_years));
  return asset.purchase_price * (1 - depreciatedRatio);
}

function depreciationRatio(asset: AssetEntry): number {
  const yearsElapsed =
    (Date.now() - new Date(asset.purchase_date).getTime()) / (365 * 24 * 3600 * 1000);
  return Math.min(1, yearsElapsed / Math.max(1, asset.depreciation_years));
}

function depreciationThisYear(asset: AssetEntry): number {
  const annual = asset.purchase_price / Math.max(1, asset.depreciation_years);
  const yearsElapsed =
    (Date.now() - new Date(asset.purchase_date).getTime()) / (365 * 24 * 3600 * 1000);
  if (yearsElapsed >= asset.depreciation_years) return 0;
  if (Math.floor(yearsElapsed) === 0) return annual * Math.min(1, yearsElapsed);
  return annual;
}

type AssetForm = {
  name: string;
  category: AssetCategory;
  purchase_date: string;
  purchase_price: number;
  depreciation_years: number;
  status: AssetStatus;
  note: string;
};

const emptyForm = (): AssetForm => ({
  name: "",
  category: "equipment",
  purchase_date: new Date().toISOString().slice(0, 10),
  purchase_price: 0,
  depreciation_years: 5,
  status: "active",
  note: "",
});

type TabKey = "active" | "broken" | "disposed" | "all";

export default function AssetsPage() {
  const [assets, setAssets] = useState<AssetEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("active");
  const [showAdd, setShowAdd] = useState(false);
  const [showStatus, setShowStatus] = useState<AssetEntry | null>(null);
  const [form, setForm] = useState<AssetForm>(emptyForm());
  const [editId, setEditId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.assets.list();
      setAssets(r.assets || []);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (tab === "all") return assets;
    return assets.filter((a) => a.status === tab);
  }, [assets, tab]);

  const totalOriginal = assets.reduce((s, a) => s + a.purchase_price, 0);
  const totalCurrent = assets.reduce((s, a) => s + currentValue(a), 0);
  const totalDepYear = assets.reduce((s, a) => s + depreciationThisYear(a), 0);

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm());
    setShowAdd(true);
  };

  const openEdit = (a: AssetEntry) => {
    setEditId(a.id);
    setForm({
      name: a.name,
      category: a.category,
      purchase_date: a.purchase_date,
      purchase_price: a.purchase_price,
      depreciation_years: a.depreciation_years,
      status: a.status,
      note: a.note || "",
    });
    setShowAdd(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || form.purchase_price <= 0) {
      toast.error("Nhập tên tài sản và giá mua > 0");
      return;
    }
    try {
      if (editId) {
        await api.assets.update(editId, {
          name: form.name,
          category: form.category,
          purchase_date: form.purchase_date,
          purchase_price: form.purchase_price,
          depreciation_years: form.depreciation_years,
          status: form.status,
          note: form.note,
        });
        toast.success("Đã cập nhật");
      } else {
        await api.assets.create({
          name: form.name,
          category: form.category,
          purchase_date: form.purchase_date,
          purchase_price: form.purchase_price,
          depreciation_years: form.depreciation_years,
          status: form.status,
          note: form.note,
        });
        toast.success("Đã thêm tài sản");
      }
      setShowAdd(false);
      load();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const handleStatusChange = async (newStatus: AssetStatus) => {
    if (!showStatus) return;
    try {
      await api.assets.update(showStatus.id, { status: newStatus });
      toast.success("Đã cập nhật trạng thái");
      setShowStatus(null);
      load();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!await confirm({ title: "Xoá tài sản", message: "Xoá tài sản này?", danger: true, confirmLabel: "Xoá" })) return;
    try {
      await api.assets.delete(id);
      toast.success("Đã xoá");
      load();
    } catch (e: unknown) {
      toast.error((e as Error).message);
    }
  };

  const columns: Column<AssetEntry>[] = [
    { key: "name", header: "Tên", render: (a) => a.name },
    { key: "category", header: "Loại", render: (a) => catLabel(a.category) },
    { key: "purchase_date", header: "Ngày mua", render: (a) => formatDate(a.purchase_date) },
    { key: "purchase_price", header: "Giá mua", render: (a) => formatVND(a.purchase_price) },
    {
      key: "current_value",
      header: "Giá hiện tại",
      render: (a) => (
        <div>
          <div>{formatVND(currentValue(a))}</div>
          <div className="progress-bar" style={{ marginTop: 4 }}>
            <div
              className="progress-fill"
              style={{ width: `${depreciationRatio(a) * 100}%` }}
              title={`Khấu hao ${Math.round(depreciationRatio(a) * 100)}%`}
            />
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Trạng thái",
      render: (a) => <Badge variant={STATUS_MAP[a.status].variant}>{STATUS_MAP[a.status].label}</Badge>,
    },
    {
      key: "actions",
      header: "",
      render: (a) => (
        <div style={{ display: "flex", gap: 4 }}>
          <button className="btn-icon" title="Sửa" onClick={() => openEdit(a)}>
            <span className="material-symbols-outlined">edit</span>
          </button>
          <button className="btn-icon" title="Đổi trạng thái" onClick={() => setShowStatus(a)}>
            <span className="material-symbols-outlined">swap_horiz</span>
          </button>
          <button className="btn-icon" title="Xóa" onClick={() => handleDelete(a.id)}>
            <span className="material-symbols-outlined">delete</span>
          </button>
        </div>
      ),
    },
  ];

  const TABS: { key: TabKey; label: string }[] = [
    { key: "active", label: "Đang dùng" },
    { key: "broken", label: "Hỏng" },
    { key: "disposed", label: "Đã thanh lý" },
    { key: "all", label: "Tất cả" },
  ];

  return (
    <div className="page">
      {/* Tab nav */}
      <div className="tab-row">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tab-btn ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* KPI */}
      <div className="kpi-grid">
        <KpiCard label="Tổng nguyên giá" value={formatVND(totalOriginal)} />
        <KpiCard label="Giá trị hiện tại" value={formatVND(totalCurrent)} dotColor="ok" />
        <KpiCard label="Khấu hao năm nay" value={formatVND(totalDepYear)} dotColor="warn" />
      </div>

      {/* Table */}
      <GlassPanel
        title="Danh sách tài sản"
        badge={`${filtered.length}`}
        badgeVariant="info"
      >
        <div style={{ marginBottom: 12 }}>
          <button className="btn primary" onClick={openAdd}>
            <span className="material-symbols-outlined">add</span> Thêm tài sản
          </button>
        </div>
        {loading ? (
          <SkeletonTable rows={5} cols={7} />
        ) : error ? (
          <ErrorState error={error} onRetry={load} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(a) => a.id}
            emptyMessage="Chưa có tài sản nào. Thêm để theo dõi khấu hao và quản lý."
          />
        )}
      </GlassPanel>

      {/* Modal Thêm / Sửa */}
      <Modal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        title={editId ? "Sửa tài sản" : "Thêm tài sản"}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn" onClick={() => setShowAdd(false)}>Hủy</button>
            <button className="btn primary" onClick={handleSubmit}>
              {editId ? "Cập nhật" : "Thêm"}
            </button>
          </div>
        }
      >
        <div className="form-grid">
          <h4 className="form-section-title">Cơ bản</h4>
          <label className="field">
            <span>Tên tài sản</span>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="VD: Máy pha cà phê"
            />
          </label>
          <label className="field">
            <span>Loại</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as AssetCategory })}>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Ghi chú</span>
            <input
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Tùy chọn"
            />
          </label>

          <h4 className="form-section-title">Tài chính</h4>
          <label className="field">
            <span>Ngày mua</span>
            <input
              type="date"
              value={form.purchase_date}
              onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Giá mua (VNĐ)</span>
            <input
              type="number"
              min={0}
              value={form.purchase_price || ""}
              onChange={(e) => setForm({ ...form, purchase_price: Number(e.target.value) })}
            />
          </label>

          <h4 className="form-section-title">Khấu hao</h4>
          <label className="field">
            <span>Số năm khấu hao</span>
            <input
              type="number"
              min={1}
              max={50}
              value={form.depreciation_years}
              onChange={(e) => setForm({ ...form, depreciation_years: Number(e.target.value) || 5 })}
            />
          </label>
        </div>
      </Modal>

      {/* Modal Cập nhật trạng thái */}
      <Modal
        open={!!showStatus}
        onClose={() => setShowStatus(null)}
        title="Cập nhật trạng thái"
        width={400}
      >
        {showStatus && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p>Tài sản: <strong>{showStatus.name}</strong></p>
            <p>Trạng thái hiện tại: <Badge variant={STATUS_MAP[showStatus.status].variant}>{STATUS_MAP[showStatus.status].label}</Badge></p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(["active", "broken", "disposed"] as AssetStatus[])
                .filter((s) => s !== showStatus.status)
                .map((s) => (
                  <button key={s} className="btn" onClick={() => handleStatusChange(s)}>
                    Chuyển → {STATUS_MAP[s].label}
                  </button>
                ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
